/*
 * Top-level SPA shell. Routes are wired via react-router-dom.
 *
 * Public routes (Squarespace + SWA):
 *   /                 — tool index (SWA-only landing page)
 *   /plan-builder     — Plan Builder
 *   /ddw              — Due Diligence Workbench
 *   /stay-or-go       — Stay or Go? Diagnostic
 *
 * Admin routes (SWA only — auth gating is server-side via
 * staticwebapp.config.json):
 *   /admin/ddw         — DDW admin
 *   /admin/stay-or-go  — Stay or Go? admin
 *
 * Analytics route (SWA only — `/analytics/*` allows admin or analyst):
 *   /analytics         — Cross-tool analytics dashboard
 *
 * The Squarespace build wraps each tool in its own HTML shell (Phase 7
 * config). This file is what runs inside the SWA bundle.
 */

import { useEffect } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { api } from '@shared/api/client';
import { AppNav, AppFooter } from '@shared/components';
import { getClientId } from '@shared/clientId';
import { PrivacyBanner, PrivacyPolicy } from '@shared/privacy';
import { PlanBuilder, PlanBuilderResultsView } from '@tools/plan-builder';
import { DdwTool } from '@tools/ddw';
import { DdwAdmin } from '@admin/ddw';
import { StayOrGoTool } from '@tools/stay-or-go';
import { StayOrGoAdmin } from '@admin/stay-or-go';
import { AnalyticsDashboard } from '@analytics/index';
import { Landing } from './landing/Landing';

export default function App() {
  // App-boot side effects (previously triggered from the old Home page):
  // flush any queued outbound submissions and ensure the anonymous client id
  // exists. These belong at the app root, not on any single route.
  useEffect(() => {
    void api.drainQueue();
    getClientId();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public marketing landing — standalone page with its own nav +
            footer, so it renders WITHOUT the shared AppNav/AppFooter chrome. */}
        <Route path="/" element={<Landing />} />

        {/* Every other route shares the navy AppNav + dark AppFooter. */}
        <Route element={<AppChrome />}>
          <Route path="/plan-builder" element={<PlanBuilder />} />
          <Route
            path="/plan-builder/results/:submissionId"
            element={<PlanBuilderResultsView />}
          />
          <Route path="/ddw" element={<DdwTool />} />
          <Route path="/admin/ddw" element={<DdwAdmin />} />
          <Route path="/stay-or-go" element={<StayOrGoTool />} />
          <Route path="/admin/stay-or-go" element={<StayOrGoAdmin />} />
          <Route path="/analytics" element={<AnalyticsDashboard />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Route>

        {/* Unknown paths fall back to the landing. */}
        <Route path="*" element={<Landing />} />
      </Routes>
      <PrivacyBanner />
    </BrowserRouter>
  );
}

// Shared chrome layout for non-landing routes: the sticky navy nav and the
// dark references footer wrap the routed page via <Outlet />.
function AppChrome() {
  return (
    <>
      <AppNav />
      <Outlet />
      <AppFooter />
    </>
  );
}
